import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { Tooltip } from "@mui/material";
import Swal from "sweetalert2";
import { Alert } from "react-bootstrap";
import DeleteCsrd from "./components/DeleteCsrd";
import "./table.css";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import { useNavigate } from "react-router-dom";
import usePermissions from "./hooks/usePermissions";
import { PERMISSIONS } from "./constants/permissions";

function ReportTable({ savedReports }) {
  const { has: hasPermission } = usePermissions();
  const canStartAssessment = hasPermission(PERMISSIONS.START_ASSESSMENTS);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    reportTitle: { value: null, matchMode: FilterMatchMode.CONTAINS },
    reportType: { value: null, matchMode: FilterMatchMode.CONTAINS },
    reportStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
    completionTimeline: { value: null, matchMode: FilterMatchMode.CONTAINS },
    reportingPeriod: { value: null, matchMode: FilterMatchMode.CONTAINS },
    updatedBy: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [deleteMessage, setDeleteMessage] = useState(null);
  const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [deletename, setDeleteName] = useState(null);
  const username = localStorage.getItem("userName");
  const companyid = localStorage.getItem("companyid");
  const navigate = useNavigate();

  useEffect(() => {
    fetchReports();
  }, []);

  const showDeleteModal = (id, name) => {
    setDeleteName(id);
    setDeleteMessage("Are you sure you want to delete this report " + name + "?");
    setDisplayConfirmationModal(true);
  };

  const hideConfirmationModal = () => {
    setDisplayConfirmationModal(false);
  };

  const submitDelete = async (deletename) => {
    setLoading(true);
    try {
      let res = await fetch(`${process.env.REACT_APP_BASE_API_URL}deleteReport`, {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          username: username,
          companyid: companyid,
          reportid: deletename,
        },
      });
      if (res.status === 200) {
        const responseJson = await res.json();
        if (responseJson.success == false) {
          setLoading(false);
          Swal.fire({
            width: 400,
            heightAuto: false,
            icon: "error",
            html: `<h4 style="color: #CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
                   <p style="font-size: 16px;">${responseJson.data}</p>`,
            iconHtml: '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
          });
          setDisplayConfirmationModal(false);
        } else if (responseJson.status == 400) {
          setLoading(false);
          Swal.fire({
            icon: "error",
            html: `<h4 style="color: #CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
            <p style="font-size: 16px;">${responseJson.title}</p>`,
            iconHtml: '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
          });
          setDisplayConfirmationModal(false);
        } else {
          setLoading(false);
          Swal.fire({
            icon: "success",
            html: '<h4 style="color:#407775;font-size: 20px;">Success!</h4><p style="font-size:16px;line-height: 20px;">Report deleted successfully</p>',
            iconHtml: '<img src="assets/img/successicon.png" style="width: 55px; height: 55px;" />',
          });
          fetchReports();
          setDisplayConfirmationModal(false);
        }
      } else {
        setLoading(false);
        Swal.fire({
          icon: "error",
          html: `<h4 style="color: #CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
          <p style="font-size: 16px;">Error in delete report</p>`,
          iconHtml: '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
        });
        setDisplayConfirmationModal(false);
      }
    } catch (err) {
      setLoading(false);
      console.log(err);
    }
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderHeader = () => {
    return (
      <div className="flex justify-content-end">
        <span className="p-input-icon-left">
          <img src="assets/img/search.png" className="search-i" alt="search" />
          <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Search" />
        </span>
      </div>
    );
  };

  const header = renderHeader();

  const parseDateSafely = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const normalizeReport = (raw) => {
    const reportType = raw?.reportType || raw?.type || raw?.framework || "CSRD";
    const completionTimeline = raw?.completionTimeline || raw?.timeline || raw?.completionDate || "";
    const reportStatus = raw?.reportStatus || raw?.status || "";
    const createdOrUpdated = raw?.updatedOn || raw?.updatedAt || raw?.modifiedOn || raw?.modifiedAt || raw?.createdOn || raw?.createdAt || raw?.timestamp || "";
    const isBRSRType = ["BRSR", "BRSR_CORE", "BRSR Core", "BRSR-CORE"].includes(String(reportType));
    const brsrFinancialYear =
      raw?.financialYear ||
      raw?.reportingYear ||
      raw?.financial_year ||
      raw?.fy ||
      raw?.fiscalYear ||
      raw?.step1Data?.financialYear ||
      raw?.meta?.financialYear ||
      "";
    const reportingPeriod = isBRSRType ? brsrFinancialYear || raw?.reportingPeriod || "" : raw?.reportingPeriod || "";

    return {
      ...raw,
      reportType,
      completionTimeline,
      reportStatus,
      reportingPeriod,
      _sortDate: createdOrUpdated,
    };
  };

  const getReportKey = (row) => String(row?.rowKey || row?.reportId || row?.id || `${row?.reportTitle || ""}_${row?.blobStorageLocation || ""}`);

  const StatusIcon = ({ type, color = "#FAB430" }) => {
    if (type === "overdue") {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <g fill={color}>
            <path d="m12 1a11 11 0 0 0 -11 11 1 1 0 0 0 2 0 9 9 0 1 1 8.09 9 1 1 0 0 0 -1.09.91 1 1 0 0 0 .91 1.09c.36 0 .72.05 1.09.05a11 11 0 0 0 0-22.05z" />
            <path d="m12 5a1 1 0 0 0 -1 1v6a1 1 0 0 0 1 1h4a1 1 0 0 0 0-2h-3v-5a1 1 0 0 0 -1-1z" />
            <path d="m7 17v-4a1 1 0 0 0 -2 0v4a1 1 0 0 0 2 0z" />
            <circle cx="6" cy="21.5" r="1.5" />
          </g>
        </svg>
      );
    }

    if (type === "complete") {
      return (
        <svg width="16" height="16" viewBox="0 0 512 512" aria-hidden="true">
          <path
            fill={color}
            d="m369.164062 174.769531c7.8125 7.8125 7.8125 20.476563 0 28.285157l-134.171874 134.175781c-7.8125 7.808593-20.472657 7.808593-28.285157 0l-63.871093-63.875c-7.8125-7.808594-7.8125-20.472657 0-28.28125 7.808593-7.8125 20.472656-7.8125 28.28125 0l49.730468 49.730469 120.03125-120.035157c7.8125-7.808593 20.476563-7.808593 28.285156 0zm142.835938 81.230469c0 141.503906-114.515625 256-256 256-141.503906 0-256-114.515625-256-256 0-141.503906 114.515625-256 256-256 141.503906 0 256 114.515625 256 256zm-40 0c0-119.394531-96.621094-216-216-216-119.394531 0-216 96.621094-216 216 0 119.394531 96.621094 216 216 216 119.394531 0 216-96.621094 216-216zm0 0"
          />
        </svg>
      );
    }

    // in progress
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill={color}
          fillRule="evenodd"
          clipRule="evenodd"
          d="m9.81934.197831c.70786-.1300248 1.43676-.197831 2.18066-.197831 6.6274 0 12 5.37258 12 12 0 6.6274-5.3726 12-12 12-.7439 0-1.4728-.0678-2.18066-.1978-.5432-.0998-.90266-.621-.80289-1.1642.09978-.5432.62102-.9027 1.16425-.8029.5892.1082 1.1972.1649 1.8193.1649 5.5228 0 10-4.4772 10-10 0-5.52285-4.4772-10-10-10-.6221 0-1.2301.05669-1.8193.16492-.54323.09978-1.06447-.25968-1.16425-.80288-.09977-.543198.25969-1.064432.80289-1.164209zm-2.45373 1.773049c.27654.47806.11318 1.08979-.36488 1.36633-1.3642.78914-2.52525 1.8917-3.38413 3.20896-.30164.46264-.92121.59315-1.38384.2915-.46264-.30164-.59314-.92121-.2915-1.38384 1.02982-1.57945 2.42146-2.90114 4.05801-3.84783.47806-.27655 1.08979-.11318 1.36634.36488zm10.32129 6.30237c.4014.37936.4192 1.01227.0399 1.41365l-6.4331 6.8063c-.6387.6757-1.69874.6757-2.3374 0l-2.68305-2.8387c-.37937-.4014-.36152-1.0343.03985-1.4137.40138-.3793 1.03429-.3615 1.41365.0399l2.39825 2.5374 6.1482-6.505c.3794-.40137 1.0123-.41922 1.4137-.03985zm-7.2771 6.84615-.0005-.0005zm-8.8981-6.59344c.53795.12502.8727.66246.74768 1.20041-.16953.72943-.25938 1.49043-.25938 2.27363s.08985 1.5442.25938 2.2736c.12502.538-.20973 1.0754-.74768 1.2004-.537944.1251-1.075386-.2097-1.200407-.7476-.203781-.8769-.311293-1.7897-.311293-2.7264s.107512-1.8495.311293-2.72637c.125021-.53795.662463-.87269 1.200407-.74767zm.72106 8.63634c.46263-.3016 1.0822-.1711 1.38384.2915.85888 1.3173 2.01993 2.4198 3.38413 3.209.47806.2765.64142.8883.36488 1.3663-.27655.4781-.88828.6414-1.36634.3649-1.63655-.9467-3.02819-2.2684-4.05801-3.8478-.30164-.4627-.17114-1.0822.2915-1.3839z"
        />
      </svg>
    );
  };

  async function fetchReports() {
    const companyid = localStorage.getItem("companyid");
    var myHeaders = new Headers();
    myHeaders.append("companyid", companyid);
    myHeaders.append("username", username);

    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow",
    };

    // Fetch all report types in parallel and merge
    const typesToFetch = ["CSRD", "BRSR", "BRSR_CORE", "BRSR Core", "BRSR-CORE"];
    setLoading(true);
    try {
      const typedResults = await Promise.all(
        typesToFetch.map((t) => {
          const url = `${process.env.REACT_APP_BASE_API_URL}allreports?reportType=${encodeURIComponent(t)}`;
          return fetch(url, requestOptions).then((r) => r.json());
        }),
      );

      // Also fetch unfiltered reports as a fallback because some backends ignore/expect different reportType values.
      const allRes = await fetch(`${process.env.REACT_APP_BASE_API_URL}allreports`, requestOptions);
      const allJson = await allRes.json();

      const mergedRaw = [...typedResults.flatMap((json) => (json.success === false || json.status === 400 ? [] : json.data || [])), ...(allJson.success === false || allJson.status === 400 ? [] : allJson.data || [])];

      const dedupedMap = new Map();
      mergedRaw.forEach((r) => {
        const normalized = normalizeReport(r);
        dedupedMap.set(getReportKey(normalized), normalized);
      });

      const merged = Array.from(dedupedMap.values()).sort((a, b) => {
        const da = parseDateSafely(a?._sortDate)?.getTime() || 0;
        const db = parseDateSafely(b?._sortDate)?.getTime() || 0;
        return db - da;
      });

      setReports(merged);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  }

  const typeBodyTemplate = (rowData) => {
    const t = rowData.reportType || rowData.type || "CSRD";
    const config = {
      BRSR_CORE: { label: "BRSR", bg: "#f0f4ff", color: "#5b6abf" },
      BRSR: { label: "BRSR", bg: "#f0f4ff", color: "#5b6abf" },
      CSRD: { label: "CSRD", bg: "#fff4e5", color: "#b36800" },
    };
    const { label, bg, color } = config[t] || config["CSRD"];
    return (
      <span
        style={{
          display: "inline-block",
          padding: "2px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "600",
          background: bg,
          color: color,
        }}
      >
        {label}
      </span>
    );
  };

  const statusBodyTemplate = (rowData) => {
    const completion = parseDateSafely(rowData?.completionTimeline);
    const now = new Date();
    const statusText = String(rowData?.reportStatus || rowData?.status || "").toLowerCase();

    // Consider multiple possible backend progress fields for "all selected modules/questions answered & saved".
    const completionPercentCandidates = [rowData?.completionPercent, rowData?.completionPercentage, rowData?.progress, rowData?.progressPercent, rowData?.reportCompletion].map((v) => (v == null || v === "" ? NaN : parseFloat(v))).filter((v) => !isNaN(v) && isFinite(v));
    const maxCompletionPercent = completionPercentCandidates.length ? Math.max(...completionPercentCandidates) : -1;

    const selectedModules = parseFloat(rowData?.selectedModules ?? rowData?.selectedModuleCount ?? rowData?.totalModules ?? NaN);
    const answeredModules = parseFloat(rowData?.answeredModules ?? rowData?.completedModules ?? rowData?.savedModules ?? NaN);
    const selectedQuestions = parseFloat(rowData?.selectedQuestions ?? rowData?.totalQuestions ?? NaN);
    const answeredQuestions = parseFloat(rowData?.answeredQuestions ?? rowData?.savedQuestions ?? rowData?.completedQuestions ?? NaN);

    const modulesComplete = !isNaN(selectedModules) && selectedModules > 0 && !isNaN(answeredModules) && answeredModules >= selectedModules;
    const questionsComplete = !isNaN(selectedQuestions) && selectedQuestions > 0 && !isNaN(answeredQuestions) && answeredQuestions >= selectedQuestions;
    const progressComplete = maxCompletionPercent >= 100;

    const isDone = ["completed", "complete", "published", "closed"].includes(statusText) || modulesComplete || questionsComplete || progressComplete;
    const isOverdue = completion && completion < now && !isDone;
    const label = isOverdue ? "Overdue" : isDone ? "Complete" : "In progress";
    const color = isOverdue ? "#CC2229" : "#FAB430";
    const iconType = isOverdue ? "overdue" : isDone ? "complete" : "inprogress";

    return (
      <span style={{ color, fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
        <StatusIcon type={iconType} color={color} />
        {label}
      </span>
    );
  };

  const openStatusDashboard = (rowData) => {
    const blobLocation = rowData?.blobStorageLocation || "";
    const blobFileName = blobLocation ? blobLocation.split("/").pop() : "";
    const fileName = rowData?.fileName || blobFileName || "";
    localStorage.setItem(
      "reportStatusData",
      JSON.stringify({
        reportName: rowData?.reportTitle || "Untitled Report",
        completionDate: rowData?.completionTimeline || "",
      }),
    );
    localStorage.setItem("statusReportFile", fileName);
    navigate(`/reportdashboard?file=${encodeURIComponent(fileName)}`);
  };

  return (
    <div>
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>

      <div className="datatable-responsive">
        <DataTable
          value={reports}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 20, 25, 50]}
          tableStyle={{ minWidth: "50rem" }}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          dataKey="id"
          filters={filters}
          filterDisplay="row"
          globalFilterFields={["reportTitle", "reportStatus", "completionTimeline", "reportingPeriod", "updatedBy", "reportType"]}
          header={header}
          emptyMessage="No data found."
        >
          <Column
            header=""
            body={(rowData) => {
              const reportType = rowData.reportType || rowData.type || "CSRD";
              const isBRSR = reportType === "BRSR" || reportType === "BRSR_CORE";
              const editUrl = isBRSR ? `/brsr/new-report?mode=edit&reportId=${encodeURIComponent(rowData.rowKey || rowData.reportId || "")}` : `/editcsrd?url=${encodeURIComponent(rowData.blobStorageLocation || "")}&file=${encodeURIComponent(rowData.fileName || "")}`;
              return (
                <div>
                  <Tooltip title={canStartAssessment ? "Edit" : "You don't have permission to edit reports"} arrow>
                    <Link to={canStartAssessment ? editUrl : "#"} onClick={canStartAssessment ? undefined : (e) => e.preventDefault()}>
                      <img src="./assets/img/reports/edit.png" alt="Edit Icon" className="edit-icon actions-icon" style={{ opacity: canStartAssessment ? 1 : 0.4, cursor: canStartAssessment ? "pointer" : "not-allowed" }} />
                    </Link>
                  </Tooltip>
                  <Tooltip title={isBRSR ? "Status dashboard (BRSR) coming soon" : "Status"} arrow>
                    <img
                      src="./assets/img/reports/Status.svg"
                      alt="Status Icon"
                      className="status-icon actions-icon"
                      onClick={isBRSR ? undefined : () => openStatusDashboard(rowData)}
                      style={{
                        cursor: isBRSR ? "not-allowed" : "pointer",
                        opacity: isBRSR ? 0.45 : 1,
                        pointerEvents: "auto",
                      }}
                    />
                  </Tooltip>
                  <Tooltip title={canStartAssessment ? "Delete" : "You don't have permission to delete reports"} arrow>
                    <img src="./assets/img/reports/delete.png" alt="Delete Icon" className="delete-icon actions-icon" style={{ opacity: canStartAssessment ? 1 : 0.4, cursor: canStartAssessment ? "pointer" : "not-allowed" }} onClick={canStartAssessment ? () => showDeleteModal(rowData.rowKey, rowData.reportTitle) : undefined} />
                  </Tooltip>
                </div>
              );
            }}
            style={{ minWidth: "8rem", paddingLeft: "30px" }}
          />

          <Column sortable field="reportTitle" header="Reports" filter filterPlaceholder="Filter" style={{ minWidth: "8rem" }} />

          <Column sortable field="reportType" header="Framework" filter filterPlaceholder="Filter" body={typeBodyTemplate} style={{ minWidth: "8rem" }} />

          <Column sortable field="reportStatus" header="Status" filter filterPlaceholder="Filter" body={statusBodyTemplate} style={{ minWidth: "5rem" }} />

          <Column sortable field="completionTimeline" header="Completion Timeline" filter filterPlaceholder="Filter" style={{ minWidth: "8rem" }} />

          <Column sortable field="reportingPeriod" header="Reporting Period" filter filterPlaceholder="Filter" style={{ minWidth: "14rem" }} />

          <Column sortable field="updatedBy" header="Creator" filter filterPlaceholder="Filter" style={{ minWidth: "10rem", paddingRight: "30px" }} />
        </DataTable>
      </div>

      <div className="container pd-0">
        {successMessage && <Alert variant="success">{successMessage}</Alert>}
        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
        <DeleteCsrd confirmModal={submitDelete} showModal={displayConfirmationModal} hideModal={hideConfirmationModal} message={deleteMessage} deletename={deletename} />
      </div>
    </div>
  );
}

export default ReportTable;
